import { useField, useFormikContext } from "formik";
import { useEffect, useMemo, useRef } from "react";

interface UseFormikFieldArgs<Value> {
  name: string;
  onChange?: (value: Value) => void;
  onBlur?: <E>(event: E) => void;
}

export const useFormikField = <Value>({ name, onChange, onBlur }: UseFormikFieldArgs<Value>) => {
  const formik = useFormikContext();
  const [field, meta, { setValue, setTouched }] = useField<Value>(name);

  const helpers = useRef({ setValue, setTouched });
  useEffect(() => {
    helpers.current = { setTouched, setValue };
  }, [setValue, setTouched]);

  const error = meta.error;

  const isErrorVisible = (meta.touched || formik.submitCount > 0) && Boolean(error);

  const actions = useMemo(() => {
    const handleChange = (value: Value) => {
      helpers.current.setValue(value, formik.validateOnChange);
      onChange?.(value);
    };

    const handleBlur = <E>(event: E) => {
      helpers.current.setTouched(true, formik.validateOnBlur);
      onBlur?.(event);
    };

    return {
      handleChange,
      handleBlur,
    };
  }, [formik.validateOnChange, onChange, formik.validateOnBlur, onBlur]);

  return {
    value: field.value,
    error,
    isErrorVisible,
    ...actions,
  };
};
